/**
 * RulesExpander — reads *.rules.json, expands $ref+bind in observers/effects/conditions,
 * and emits { effects, observers, rules } for the FormSchema.
 */

import type {
  RawRulesFile,
  FormLoaderContext,
} from './types';
import type { FormEffect, FormObserver, FormRule } from '../types/block';
import { resolveRef, applyBindings } from './uriResolver';

interface ExpandedRules {
  effects: FormEffect[];
  observers: FormObserver[];
  rules: FormRule[];
}

function expandEntry<T>(
  entry: Record<string, unknown>,
  ctx: FormLoaderContext,
  localDoc: Record<string, unknown>,
): T {
  if (entry.$ref && typeof entry.$ref === 'string') {
    const resolved = resolveRef(entry.$ref as string, ctx, localDoc);
    if (resolved == null || typeof resolved !== 'object') {
      throw new Error(`[RulesExpander] $ref "${entry.$ref}" resolved to non-object: ${typeof resolved}`);
    }

    const { $ref: _discard, bind, ...overrides } = entry;
    let expanded = { ...resolved as Record<string, unknown>, ...overrides };

    if (bind && typeof bind === 'object') {
      expanded = applyBindings(expanded, bind as Record<string, string>) as Record<string, unknown>;
    }

    return expanded as T;
  }

  return entry as unknown as T;
}

function isEffectLike(entry: Record<string, unknown>): boolean {
  return 'watch' in entry && ('clear' in entry || 'set' in entry || 'copy' in entry || 'format' in entry || 'action' in entry);
}

function isObserverLike(entry: Record<string, unknown>): boolean {
  return 'watch' in entry && 'lookup' in entry && 'mapTo' in entry;
}

function isRuleLike(entry: Record<string, unknown>): boolean {
  return 'target' in entry && 'rule' in entry && 'message' in entry;
}

/**
 * Convert JSON Pointer paths (`#/properties/foo`) to flat dot-paths (`foo`).
 * Handles nested pointers: `#/properties/work/properties/addr` → `work.addr`.
 */
function pointerToPath(pointer: string): string {
  return pointer.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
}

function normalizePointersDeep(obj: unknown): unknown {
  if (obj == null || typeof obj !== 'object') {
    if (typeof obj === 'string' && obj.startsWith('#/properties/')) {
      return pointerToPath(obj);
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(normalizePointersDeep);
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const normalizedKey = (typeof k === 'string' && k.startsWith('#/properties/'))
      ? pointerToPath(k)
      : k;

    if (
      (normalizedKey === 'field' || normalizedKey === 'target' || normalizedKey === 'watch') &&
      typeof v === 'string' &&
      v.startsWith('#/properties/')
    ) {
      out[normalizedKey] = pointerToPath(v);
    } else if (typeof v === 'object' && v !== null) {
      out[normalizedKey] = normalizePointersDeep(v);
    } else if (typeof v === 'string' && v.startsWith('#/properties/')) {
      out[normalizedKey] = pointerToPath(v);
    } else {
      out[normalizedKey] = v;
    }
  }
  return out;
}

export function expandRules(
  rulesFile: RawRulesFile,
  ctx: FormLoaderContext,
): ExpandedRules {
  const effects: FormEffect[] = [];
  const observers: FormObserver[] = [];
  const rules: FormRule[] = [];
  const localDoc = rulesFile as unknown as Record<string, unknown>;

  if (rulesFile.effects) {
    for (const entry of rulesFile.effects) {
      const expanded = expandEntry<Record<string, unknown>>(entry, ctx, localDoc);
      const normalized = normalizePointersDeep(expanded) as Record<string, unknown>;

      // Convert trigger/apply effects to conditional validation rules.
      // Pattern: { trigger: { field, ... }, apply: { field, set: { required: true } } }
      // becomes: FormRule { target, rule: "required", condition: trigger }
      if ('trigger' in normalized && 'apply' in normalized && !('watch' in normalized)) {
        const trigger = normalized.trigger as Record<string, unknown>;
        const apply = normalized.apply as Record<string, unknown>;
        const applySet = apply?.set as Record<string, unknown> | undefined;
        if (applySet?.required === true && apply.field) {
          rules.push({
            id: (normalized.id as string) ?? `generated_${rules.length}`,
            target: apply.field as string,
            rule: 'required',
            message: 'Required field',
            condition: trigger,
          } as unknown as FormRule);
          continue;
        }
      }

      effects.push(normalized as FormEffect);
    }
  }

  if (rulesFile.observers) {
    for (const entry of rulesFile.observers) {
      const expanded = expandEntry<Record<string, unknown>>(entry, ctx, localDoc);
      const normalized = normalizePointersDeep(expanded) as Record<string, unknown>;

      // FormObserver.watch is typed as `string` but JSON authors may write `["field"]`.
      // Flatten single-element arrays to a plain string.
      if (Array.isArray(normalized.watch)) {
        normalized.watch = normalized.watch[0] ?? '';
      }

      observers.push(normalized as unknown as FormObserver);
    }
  }

  if (rulesFile.rules) {
    for (const entry of rulesFile.rules) {
      const expanded = expandEntry<FormRule>(entry, ctx, localDoc);
      rules.push(normalizePointersDeep(expanded) as FormRule);
    }
  }

  if (rulesFile.templates) {
    for (const [_templateId, template] of Object.entries(rulesFile.templates)) {
      if (template == null || typeof template !== 'object') continue;
      const templateObj = template as Record<string, unknown>;

      const normalized = normalizePointersDeep(templateObj);
      if (isObserverLike(normalized as Record<string, unknown>)) {
        observers.push(normalized as unknown as FormObserver);
      } else if (isEffectLike(normalized as Record<string, unknown>)) {
        effects.push(normalized as unknown as FormEffect);
      } else if (isRuleLike(normalized as Record<string, unknown>)) {
        rules.push(normalized as unknown as FormRule);
      }
    }
  }

  return { effects, observers, rules };
}
